/* eslint no-shadow: ["error", { "allow": ["state", "getters"] }] */
import Vue from 'vue'
import {
  firebase, firebaseAuth, usersCollection,
} from '../../../firebase'
import router from '../../router'

const state = {
  user: null,
  userCampaigns: [],
  line: null,
}

const isEmpty = obj => [Object, Array].includes((obj || {}).constructor) && !Object.entries((obj || {})).length

const keysList = {
  user: [
    'displayName',
    'fullName',
    'createFrom',
    'email',
    'language',
    'phoneNumber',
    'photoURL',
    'tags',
    'updateAt',
    'claims',
    'uid',
    'campaigns',
    'emailVerified',
    'payment',
    'totalCustomers',
    'totalRecords',
  ],
}

const getters = {
  line: state => state.line,
  user: state => state.user,
  userCampaigns: state => state.userCampaigns,
}

const mutations = {
  setLine: (state, line) => {
    state.line = line
  },
  setUserCampaigns: (state, { campaigns, campaign }) => {
    if (campaigns && campaigns.constructor === Array) {
      state.userCampaigns = campaigns
    } else if (typeof campaign === 'object' && campaign !== null) {
      state.userCampaigns.push(campaign)
    }
  },
  setMutation: (state, { keyName, object, reset }) => {
    if (keyName && object === null) {
      state[keyName] = null
    } else if (keyName && object) {
      const keys = keysList[keyName]
      const obj = {}

      if (!reset && (state[keyName] === 'undefined' || state[keyName] === null)) {
        state[keyName] = {}
      }

      keys.forEach(key => {
        if (Object.prototype.hasOwnProperty.call(object, key)) {
          if (object[key] !== 'undefined' && object[key] !== null) {
            if (reset) {
              obj[key] = object[key]
            } else {
              state[keyName][key] = object[key]
            }
          }
        }
      })

      if (reset) {
        state[keyName] = obj
      }
    }
  },
}

const actions = {
  verifyPhoneNumber: async (context, params) => {
    const { phoneNumber, appVerifier } = params
    const provider = new firebase.auth.PhoneAuthProvider()
    return provider.verifyPhoneNumber(phoneNumber, appVerifier).then(verificationId => verificationId)
  },
  updateFirebaseAuth: async (args, { displayName, photoURL }) => {
    const firebaseUser = firebaseAuth.currentUser
    if (firebaseUser !== null) {
      if (displayName !== 'undefined' || photoURL !== 'undefined') {
        await firebaseUser.updateProfile({
          photoURL,
          displayName,
        })
      }
    }
  },
  updateUserTags: async (args, { uid, types }) => {
    if (uid && types && types.constructor === Array) {
      const today = Vue.moment().format('YYYY-MM-DD')
      localStorage.removeItem(today + uid)
      usersCollection.doc(uid).update({
        tags: firebase.firestore.FieldValue.arrayUnion(...types),
      })
    }
  },
  signUp: async ({ commit, dispatch }, user) => {
    await firebaseAuth.createUserWithEmailAndPassword(user.email, user.password)
      .then(userCredential => {
        dispatch('updateFirebaseAuth', { displayName: user.displayName })
        // Signed in
        if (userCredential.user.emailVerified) {
          // Email is verified
          commit('setMutation', { keyName: 'user', object: userCredential.user })
        } else {
          // Email is not verified
          firebaseAuth.currentUser.sendEmailVerification().then(() => {
          // Verification email sent.
          })
        }
      })
  },
  signIn: async ({ dispatch }, user) => new Promise((resolve, reject) => {
    firebaseAuth.signInWithEmailAndPassword(user.email, user.password).then(userCredential => {
      if (userCredential.user.emailVerified) {
        const today = Vue.moment().format('YYYY-MM-DD')
        localStorage.removeItem(today + userCredential.user.uid)
        dispatch('fetchUserProfile', { ...userCredential.user, signIn: true }).then(() => {
          router.push('/')
        })
      } else {
        firebaseAuth.signOut()
        const error = {
          code: 'emailNotVerified',
          message: 'Email is not verified, ',
        }
        reject(error)
      }
    }).catch(error => {
      reject(error)
    })
  }),
  resendEmailVerification: async () => {
    firebase.auth().currentUser.sendEmailVerification()
      .then(() => {
      // Verification email sent.
      })
      .catch(() => {
      // Error occurred. Inspect error.code.
      })
  },
  signOut: async () => {
    try {
      await firebaseAuth.signOut()
      router.push('/login')
    } catch {
      //
    }
  },
  forgotPassword: async (ctx, email) => {
    firebaseAuth.sendPasswordResetEmail(email)
  },
  fetchUserProfile: async ({ commit, dispatch }, params) => new Promise(resolve => {
    let user = (params) || firebaseAuth.currentUser
    if (!isEmpty(user) && user.uid) {
      const today = Vue.moment().format('YYYY-MM-DD')
      if (!isEmpty(localStorage.getItem(today + user.uid))) {
        user = JSON.parse(localStorage.getItem(today + user.uid))

        commit('setMutation', { keyName: 'user', object: user })
        commit('setUserCampaigns', { campaigns: user.campaigns })
        resolve(user)
      } else {
        usersCollection.doc(user.uid).get().then(snapshot => {
          if (snapshot && snapshot.exists) {
            user = { ...snapshot.data(), uid: user.uid }
            commit('setMutation', { keyName: 'user', object: user })
            commit('setUserCampaigns', { campaigns: user.campaigns })

            localStorage.setItem(today + user.uid, JSON.stringify(user))
            resolve(user)
          } else if ((user.displayName || user.email) && user.signIn) {
            // basic, premium, enterprise
            // status: active inactive, suspanded, deleted
            const payment = {
              type: 'basic',
              credits: '500',
              freeCredits: '0',
              bonusCredits: '0',
              dueDate: null,
              nextDate: null,
            }

            // Signed up but user account wasn't created
            usersCollection.doc(user.uid).set({
              fullName: user.displayName,
              email: user.email,
              campaigns: [],
              status: 'active',
              type: 'admin',
              totalCustomers: 0,
              totalRecords: 0,
              payment,
              createAt: firebase.firestore.FieldValue.serverTimestamp(),
              updateAt: firebase.firestore.FieldValue.serverTimestamp(),
            }).then(() => {
              dispatch('fetchUserProfile')
            })
          }
        })
      }
    }
  }),
}

export default {
  state,
  mutations,
  getters,
  actions,
}
